"use client";

// src/components/organisms/shared/posthog-identify.tsx
// The only source of PostHog `identify` calls. Mounted once in
// the dashboard layout (every authenticated page) and once in the auth
// layout, scoped there to `/2fa`, `/kyc`, `/kyb` and `/set-password` — the
// other auth routes (`/login`, `/sign-up`, `/reset-password`, and
// `/verify-email`, which is unauthenticated) must never identify.
//
// Deliberately split into two pure, exported functions:
//   - `isIdentifyAllowedPath` — the path gate.
//   - `runIdentifyEffect` — the once-per-user-id call, i.e. the component's
//     `useEffect` body.
// This repo's Vitest config runs with `environment: "node"` (no jsdom, no
// react-test-renderer), so `useEffect` never fires under
// `renderToStaticMarkup` (see ubo-info-step.spec.ts's own note on this). The
// spec drives `runIdentifyEffect` directly instead of mounting, and asserts
// `PostHogIdentify` itself only for its render output (null, no DOM).
//
// `$set_once` carries `signup_method` alone —
// `personSetOnceFromMeta` derives it from `/api/user/meta`'s `signupMethod`,
// which is `null` unless the flags are provably correct (identity.ts).
// `first_touch_*` is NOT part of it: that half is already written once by
// `startPostHog()` in src/lib/posthog/client.ts. Do not add it here —
// that would duplicate that write.

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { authClient } from "@/lib/auth-client";
import { useUserMetaQuery } from "@/hooks/queries/use-user";
import { identifyUser } from "@/lib/posthog/client";
import {
  personSetFromMeta,
  personSetOnceFromMeta,
  type PersonMeta,
} from "@/lib/posthog/identity";

/** The authenticated auth-route allowlist for the `(auth)` layout mount. */
export const AUTH_IDENTIFY_PATHS = ["/2fa", "/kyc", "/kyb", "/set-password"] as const;

/**
 * Pure: true when `pathname` is one of `allowedPrefixes` or a `/`-bounded
 * child of one of them, or when the mount allows every path (`"all"` — the
 * dashboard layout, where every route under it is authenticated).
 */
export function isIdentifyAllowedPath(
  pathname: string | null,
  allowedPrefixes: readonly string[] | "all",
): boolean {
  if (!pathname) return false;
  if (allowedPrefixes === "all") return true;
  return allowedPrefixes.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

/**
 * The component's `useEffect` body. Calls `identifyUser` at most once per
 * user id (tracked in `lastIdentifiedUserIdRef`, backed by the component's
 * `useRef`): a re-render, a same-user navigation, or React strict mode's
 * double effect invocation never produce a second call; a different user id
 * (account switch) does. Does nothing until both a user id and the meta
 * query have resolved, so it never sends a half-empty `$set` and then a
 * second one once the data lands.
 *
 * `identifyUser` (src/lib/posthog/client.ts) already ensures no `$set`
 * leaves the browser while opted out, by returning early itself.
 * This function never re-checks that; it calls through unconditionally, so
 * the guard lives in exactly one place.
 */
export function runIdentifyEffect(
  input: {
    pathname: string | null;
    allowedPaths: readonly string[] | "all";
    userId: string | null | undefined;
    meta: PersonMeta | null | undefined;
  },
  lastIdentifiedUserIdRef: { current: string | null },
): void {
  if (!isIdentifyAllowedPath(input.pathname, input.allowedPaths)) return;
  if (!input.userId || !input.meta) return;
  if (lastIdentifiedUserIdRef.current === input.userId) return;
  lastIdentifiedUserIdRef.current = input.userId;
  identifyUser(
    input.userId,
    { ...personSetFromMeta(input.meta) },
    { ...personSetOnceFromMeta(input.meta) },
  );
}

export function PostHogIdentify({
  allowedPaths = "all",
}: {
  allowedPaths?: readonly string[] | "all";
}) {
  const pathname = usePathname();
  const { data: session } = authClient.useSession();
  // `enabled` matters because the `(auth)` layout is a root layout: this
  // component also mounts on `/login` and `/sign-up`, where an anonymous
  // visitor would otherwise fire `/api/user/meta` (401, retried once, and
  // again on every tab focus) on the app's most public pages. Under the
  // dashboard mount `allowedPaths` is `"all"`, so this is simply true once
  // the session resolves, and the query key is shared with the other
  // consumers — an unstarted observer just reads their cache.
  const { data: meta } = useUserMetaQuery({
    enabled: isIdentifyAllowedPath(pathname, allowedPaths) && Boolean(session?.user?.id),
  });
  const lastIdentifiedUserId = useRef<string | null>(null);

  useEffect(() => {
    runIdentifyEffect(
      { pathname, allowedPaths, userId: session?.user?.id, meta },
      lastIdentifiedUserId,
    );
  }, [pathname, allowedPaths, session, meta]);

  return null;
}
